import {
    Button,
    DateRangeField,
    Form,
    FormFooter,
    FormSection,
    MultiSelectField,
    Page,
    SelectOption,
    toast
} from '@7shifts/sous-chef';
import { handleError } from 'api/apiUtils';
import { getQueues } from 'api/queuesApi';
import { getLeadsContactsPerDayReport } from 'api/reportsApi';
import { useFormik } from 'formik';
import { __ } from 'i18n/translate';
import { useEffect, useState } from 'react';
import UserSelectField from 'sharedComponents/UserSelectField';
import { Queue } from 'types';
import { dateYearMonthDay } from 'utils/date';

type FormData = {
    date: { start: Date; end: Date };
    user: SelectOption<number> | null;
    queues: { id: string; value: string }[];
};

const LeadsContactsPerDayPage = () => {
    const [queues, setQueues] = useState<Queue[]>([]);

    useEffect(() => {
        getQueues()
            .then((data) => setQueues(data))
            .catch(handleError);
    }, []);

    const formik = useFormik<FormData>({
        initialValues: {
            date: {
                start: new Date(),
                end: new Date()
            },
            queues: [],
            user: null
        },
        validate: (values) => {
            const errors: Partial<Record<keyof FormData, string>> = {};
            if (!values.date?.start) errors.date = 'Data inicial obrigatória';
            if (!values.date?.end) errors.date = 'Data final obrigatória';
            if (values.date?.start && values.date?.end) {
                const diffMs = values.date.end.getTime() - values.date.start.getTime();
                const diffDays = diffMs / (1000 * 60 * 60 * 24);
                if (diffDays > 15) errors.date = 'O período não pode ultrapassar 15 dias';
            }
            if (!values.queues || values.queues.length === 0)
                errors.queues = 'Selecione ao menos uma fila';
            return errors;
        },
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(true);
            getLeadsContactsPerDayReport(
                dateYearMonthDay(values.date.start),
                dateYearMonthDay(values.date.end),
                values.queues.map((a) => a.value),
                values.user?.value || null
            )
                .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'leadsContactsPerDay.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                })
                .catch(() => {
                    toast('Something went wrong', 'danger');
                })
                .finally(() => formik.setSubmitting(false));
        }
    });

    return (
        <Page
            breadcrumbs={__('main.reports')}
            title={__('reportsPage.reportLeadsContactsPerDay')}
            subtitle={__('reportsPage.reportLeadsContactsPerDayDescription')}
        >
            <Form formik={formik}>
                <FormSection as="card">
                    <DateRangeField
                        name="date"
                        label="Período"
                        format="dd/MM/yyyy"
                    />
                    <MultiSelectField
                        name="queues"
                        placeholder={__('component.select.placeholder')}
                        label={__('main.queues')}
                        options={queues.map((queue) => ({
                            label: queue.name,
                            value: queue.id
                        }))}
                    />
                    <UserSelectField
                        name="user"
                        placeholder={__('dashboardPage.allUsers')}
                        label="Usuário"
                        onChange={(userOption) =>
                            formik.setFieldValue('user', userOption)
                        }
                    />
                </FormSection>
                <FormFooter
                    actions={{
                        primary: (
                            <Button type="submit" loading={formik.isSubmitting}>
                                {__('main.downloadCSV')}
                            </Button>
                        )
                    }}
                />
            </Form>
        </Page>
    );
};

export default LeadsContactsPerDayPage;
